export interface EducationSnapshot {
  graduationYear: number | null;
  firstLicensedYear: number | null;
  firstLicensedMonth: number | null;
  educationCredentialsConfirmedOn: string | null;
  type: number;
  country: number | null;
}

export interface EducationTalent extends EducationSnapshot {
  id: number;
  firstName?: string;
  lastName?: string;
}

export interface EducationForm {
  graduationYear: string;
  firstLicensedYear: string;
  firstLicensedMonth: number | null;
  notApplicable: boolean;
}

export const credentialMonths = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
].map((text, index) => ({ text, value: index + 1 }));

export const canMarkNotApplicable = (type: number) =>
  Number(type) === 1 || Number(type) === 2;

export const isNotApplicable = (talent: EducationSnapshot) =>
  canMarkNotApplicable(talent.type) && talent.firstLicensedYear == null &&
  talent.firstLicensedMonth == null && !!talent.educationCredentialsConfirmedOn;

export function credentialFieldLabel(talent: { type: number | string; country: number | string | null }): string {
  const base = Number(talent.type) === 1 ? "Registered / Certified" : "Licensed";
  // The review/booked queues return country names; the profile uses numeric IDs.
  const countryValue = String(talent.country ?? "").trim().toLowerCase();
  const country = ["0", "canada"].includes(countryValue) ? " in Canada"
    : ["1", "united states", "usa"].includes(countryValue) ? " in USA" : "";
  return `${base}${country} Since`;
}

export function credentialDisplay(talent: EducationSnapshot): string {
  if (talent.firstLicensedYear != null) {
    const month = credentialMonths.find(item => item.value === Number(talent.firstLicensedMonth));
    return `${month ? month.text + " " : ""}${talent.firstLicensedYear}`;
  }
  return isNotApplicable(talent) ? "Not applicable" : "Not provided";
}

export function educationSnapshot(talent: EducationSnapshot): EducationSnapshot {
  return {
    graduationYear: talent.graduationYear == null ? null : Number(talent.graduationYear),
    firstLicensedYear: talent.firstLicensedYear == null ? null : Number(talent.firstLicensedYear),
    firstLicensedMonth: talent.firstLicensedMonth == null ? null : Number(talent.firstLicensedMonth),
    educationCredentialsConfirmedOn: talent.educationCredentialsConfirmedOn || null,
    type: Number(talent.type), country: talent.country == null ? null : Number(talent.country)
  };
}

export function educationForm(talent: EducationSnapshot): EducationForm {
  return {
    graduationYear: talent.graduationYear == null ? "" : String(talent.graduationYear),
    firstLicensedYear: talent.firstLicensedYear == null ? "" : String(talent.firstLicensedYear),
    firstLicensedMonth: talent.firstLicensedMonth == null ? null : Number(talent.firstLicensedMonth),
    notApplicable: isNotApplicable(talent)
  };
}

export function validateEducationForm(form: EducationForm, current: EducationSnapshot, now = new Date()): string {
  const year = now.getUTCFullYear();
  const graduation = Number(form.graduationYear);
  if (!/^\d{4}$/.test(form.graduationYear) || graduation < 1960 || graduation > year) {
    return `Graduation Year must be between 1960 and ${year}.`;
  }
  if (form.notApplicable) {
    return canMarkNotApplicable(current.type) ? "" : "This role requires a licence date.";
  }
  const licensed = form.firstLicensedYear === "" ? null : Number(form.firstLicensedYear);
  const month = form.firstLicensedMonth;
  if (licensed != null && (!/^\d{4}$/.test(form.firstLicensedYear) || licensed < 1960 || licensed > year)) {
    return `Credential year must be between 1960 and ${year}.`;
  }
  if (licensed != null && licensed < graduation) return "Registration or licence year cannot be before Graduation Year.";
  if (month != null && (!Number.isInteger(month) || month < 1 || month > 12)) return "Choose a valid credential month.";
  if (licensed === year && month != null && month > now.getUTCMonth() + 1) return "Registration or licence date cannot be in the future.";
  const changed = licensed !== current.firstLicensedYear || month !== current.firstLicensedMonth || isNotApplicable(current);
  if ((changed || month != null) && (licensed == null || month == null)) {
    return "Choose both the credential month and year, or select Not applicable when available.";
  }
  return "";
}

export function educationUpdate(form: EducationForm, current: EducationSnapshot) {
  return {
    expected: educationSnapshot(current), graduationYear: Number(form.graduationYear),
    firstLicensedYear: form.notApplicable || form.firstLicensedYear === "" ? null : Number(form.firstLicensedYear),
    firstLicensedMonth: form.notApplicable ? null : form.firstLicensedMonth,
    firstLicensedNotApplicable: form.notApplicable
  };
}

export type EducationUpdate = ReturnType<typeof educationUpdate>;
